/**
 * Fixed Subscription Service
 * This file contains improved implementations for the subscription service to fix persistence issues.
 * Import these updated functions into src/lib/supabase.ts or use them directly.
 */

// Update these imports based on your actual project structure
import { supabase } from './supabase';
import { Subscription } from './supabase';

// Helper function to validate UUID format
function isValidUUID(str: string): boolean {
  const uuidRegex = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;
  return uuidRegex.test(str);
}

// Fixed service implementations
export const fixedSubscriptionServices = {
  // Create a new subscription with proper error handling and data formatting
  async create(subscription: Subscription) {
    try {
      // Ensure required fields are present
      if (!subscription.customer_id || !subscription.plan_id || !subscription.status || !subscription.start_date) {
        throw new Error('Missing required subscription fields');
      }

      // Enhanced validation for customer_id
      if (!subscription.customer_id) {
        throw new Error('Customer ID is required');
      } else if (subscription.customer_id === '1' || subscription.customer_id === 1) {
        throw new Error('Please select a valid customer');
      } else if (!isValidUUID(subscription.customer_id)) {
        console.error('Invalid customer_id format:', subscription.customer_id);
        throw new Error(`Invalid customer ID format (${subscription.customer_id}). Must be a valid UUID.`);
      }

      // Enhanced validation for plan_id
      if (!subscription.plan_id) {
        throw new Error('Plan ID is required');
      } else if (subscription.plan_id === '1' || subscription.plan_id === 1) {
        throw new Error('Please select a valid subscription plan');
      } else if (!isValidUUID(subscription.plan_id)) {
        console.error('Invalid plan_id format:', subscription.plan_id);
        throw new Error(`Invalid plan ID format (${subscription.plan_id}). Must be a valid UUID.`);
      }

      // Format the data correctly for Supabase
      const formattedSubscription = {
        ...subscription,
        // Format dates if necessary
        start_date: subscription.start_date,
        end_date: subscription.end_date || null,
        trial_end_date: subscription.trial_end_date || null,
        // Make sure cancel_at_period_end is boolean
        cancel_at_period_end: subscription.cancel_at_period_end || false,
      };
      
      console.log('Creating subscription with data:', formattedSubscription);
      
      const { data, error } = await supabase
        .from('subscriptions')
        .insert([formattedSubscription])
        .select();
      
      if (error) {
        console.error('Error creating subscription in Supabase:', error);
        throw error;
      }
      
      console.log('Subscription created successfully:', data);
      return { data, error };
    } catch (err: any) {
      console.error('Error in subscription creation:', err);
      return { data: null, error: err };
    }
  },

  // Update a subscription with proper error handling and data formatting
  async update(id: string, updates: Partial<Subscription>) {
    try {
      if (!id) {
        throw new Error('Subscription ID is required for updates');
      }

      // Format the data correctly for Supabase
      const formattedUpdates = {
        ...updates,
        // Format dates if present
        start_date: updates.start_date,
        end_date: updates.end_date,
        trial_end_date: updates.trial_end_date,
        // Ensure updated_at is current
        updated_at: new Date().toISOString(),
      };

      console.log(`Updating subscription ${id} with data:`, formattedUpdates);

      const { data, error } = await supabase
        .from('subscriptions')
        .update(formattedUpdates)
        .eq('id', id)
        .select();
      
      if (error) {
        console.error('Error updating subscription in Supabase:', error);
        throw error;
      }
      
      console.log('Subscription updated successfully:', data);
      return { data, error };
    } catch (err: any) {
      console.error('Error in subscription update:', err);
      return { data: null, error: err };
    }
  },

  // Delete a subscription with proper error handling
  async delete(id: string) {
    try {
      if (!id) {
        throw new Error('Subscription ID is required for deletion');
      }
      
      console.log(`Deleting subscription ${id}`);
      
      const { data, error } = await supabase
        .from('subscriptions')
        .delete()
        .eq('id', id);
      
      if (error) {
        console.error('Error deleting subscription from Supabase:', error);
        throw error;
      }
      
      console.log('Subscription deleted successfully');
      return { data, error };
    } catch (err: any) {
      console.error('Error in subscription deletion:', err);
      return { data: null, error: err };
    }
  }
};
